import React from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { usePricing } from '../context/PricingContext';
import { calculatePrice } from '../utils/pricing';
import SEO from '../components/SEO';

const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, getTotalPrice } = useCart();
  const { pricingSettings } = usePricing();
  
  const subtotal = getTotalPrice();
  const minimumOrderAmount = pricingSettings.minimumOrderAmount || 20;
  const needsMinimumOrderFee = subtotal > 0 && subtotal < minimumOrderAmount;
  const minimumOrderFee = needsMinimumOrderFee ? minimumOrderAmount - subtotal : 0;
  const adjustedSubtotal = subtotal + minimumOrderFee;
  
  // Helper function to check if item has volume discount
  const getVolumeDiscount = (item: any) => {
    if (!item.product.eligibleForVolumeDiscount || item.product.eligibleForVolumeDiscount === false) {
      return null;
    }
    
    const volumeDiscounts = pricingSettings.volumeDiscounts || [];
    for (const rule of volumeDiscounts.sort((a, b) => b.minQuantity - a.minQuantity)) {
      if (item.quantity >= rule.minQuantity) {
        return rule;
      }
    }
    return null;
  };
  
  // Calculate original price without discount
  const getOriginalUnitPrice = (item: any) => {
    const quoteRequest = {
      product: item.product,
      width: item.width,
      height: item.height,
      quantity: 1, // Calculate for single unit
      material: item.material,
      finishing: item.finishing,
      colorSpec: item.colorSpec,
      customOptions: item.customOptions
    };
    
    return calculatePrice(quoteRequest, item.product.materials, item.product.finishingOptions, []);
  };

  if (items.length === 0) {
    return (
      <>
        <SEO 
          title="Shopping Cart"
          description="Your shopping cart is empty. Browse our professional sign and banner printing products."
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4">Your Cart is Empty</h1>
        <p className="text-gray-600 mb-8">Start by adding some products to your cart.</p>
        <Link
          to="/products"
          className="inline-block bg-keystone-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-keystone-blue-800 transition-colors"
        >
          Browse Products
        </Link>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Shopping Cart"
        description={`Your shopping cart contains ${items.length} item${items.length > 1 ? 's' : ''}. Complete your order for professional sign and banner printing.`}
      />
      <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold">{item.product.name}</h3>
                        {(() => {
                          const discount = getVolumeDiscount(item);
                          if (discount) {
                            return (
                              <div className="inline-flex items-center gap-1 mt-1 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                <Tag className="h-3 w-3" />
                                <span className="font-medium">Volume discount: {(discount.discount * 100).toFixed(0)}% off</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1 -mt-1 -mr-1 text-red-500 hover:bg-red-50 rounded-lg sm:hidden"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><span className="font-medium">Size:</span> {item.width}" × {item.height}" ({((item.width * item.height) / 144).toFixed(2)} sq ft)</p>
                      {item.product.materials && item.product.materials.length > 0 && (
                        <p><span className="font-medium">Material:</span> {item.product.materials.find(m => m.id === item.material)?.name}</p>
                      )}
                      {item.product.finishingOptions && item.product.finishingOptions.length > 0 && (
                        <p><span className="font-medium">Finishing:</span> {item.product.finishingOptions.find(f => f.id === item.finishing)?.name}</p>
                      )}
                      {item.product.colorSpecs && item.product.colorSpecs.length > 0 && (
                        <p><span className="font-medium">Color:</span> {item.product.colorSpecs.find(c => c.id === item.colorSpec)?.name}</p>
                      )}
                      {item.customOptions && item.product.customOptions && (
                        <>
                          {Object.entries(item.customOptions).map(([optionId, choiceId]) => {
                            const option = item.product.customOptions?.find(opt => opt.id === optionId);
                            const choice = option?.options.find(ch => ch.id === choiceId);
                            if (option && choice) {
                              return (
                                <p key={optionId}><span className="font-medium">{option.name}:</span> {choice.name}</p>
                              );
                            }
                            return null;
                          })}
                        </>
                      )}
                      {item.customText && <p><span className="font-medium">Notes:</span> {item.customText}</p>}
                      {item.uploadedFile && <p className="text-green-600">✓ Artwork uploaded</p>}
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col items-center justify-between sm:justify-center gap-4 pt-3 sm:pt-0 border-t sm:border-t-0 sm:border-l sm:pl-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.id, Math.max(item.product.minQuantity || 1, item.quantity - 1))}
                        className={`p-1 rounded-lg ${
                          item.quantity <= (item.product.minQuantity || 1) 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:bg-gray-100'
                        }`}
                        disabled={item.quantity <= (item.product.minQuantity || 1)}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="font-medium w-12 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 rounded-lg hover:bg-gray-100"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="text-center sm:text-right">
                      {(() => {
                        const discount = getVolumeDiscount(item);
                        const originalPrice = getOriginalUnitPrice(item);
                        const hasDiscount = discount && item.price < originalPrice;
                        
                        return (
                          <>
                            <p className="font-bold text-lg">${(item.price * item.quantity).toFixed(2)}</p>
                            {hasDiscount ? (
                              <div>
                                <p className="text-xs text-gray-500 line-through">${originalPrice.toFixed(2)} each</p>
                                <p className="text-xs text-green-600 font-semibold">
                                  ${item.price.toFixed(2)} each ({(discount.discount * 100).toFixed(0)}% off)
                                </p>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-600">${item.price.toFixed(2)} each</p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg hidden sm:block"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="bg-gray-100 rounded-lg p-6 sticky top-4">
            <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
            
            <div className="space-y-2 mb-6">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {needsMinimumOrderFee && (
                <div className="flex justify-between text-yellow-600">
                  <span className="text-sm flex items-start">
                    <span>
                      Minimum order fee:
                      <span className="block text-xs text-gray-500">
                        (${minimumOrderAmount.toFixed(2)} minimum)
                      </span>
                    </span>
                    <div className="relative group ml-1">
                      <Info className="h-4 w-4 text-yellow-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="relative">
                          We require a minimum order of ${minimumOrderAmount.toFixed(2)} to help cover our production and handling costs. We apologize for any inconvenience and hope you understand. Thank you for your business!
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                        </div>
                      </div>
                    </div>
                  </span>
                  <span className="font-medium">${minimumOrderFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Tax (estimated):</span>
                <span className="font-medium">${(adjustedSubtotal * pricingSettings.taxRate).toFixed(2)}</span>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span>${(adjustedSubtotal * (1 + pricingSettings.taxRate)).toFixed(2)}</span>
              </div>
            </div>

            <Link
              to="/checkout"
              className="block w-full mt-6 bg-keystone-gold text-keystone-blue text-center py-3 rounded-lg font-semibold hover:bg-keystone-gold-600 transition-colors"
            >
              Proceed to Checkout
            </Link>
            
            <Link
              to="/products"
              className="block w-full mt-3 text-center text-keystone-blue hover:text-keystone-blue-700 hover:underline"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Cart;